// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0

import { generateClient } from "@aws-amplify/api";
import { fetchAuthSession, getCurrentUser } from "@aws-amplify/auth";
import { v4 as uuidv4 } from "uuid";

import { usageCreateRecord } from "../graphql/usageCostAllocation/mutations";
import { UsageRecord, UsageRecordInput } from "../types/usageCostAllocation";

/**
 * Calculate estimated cost by multiplying metric value by cost rate.
 */
export function calculateEstimatedCost(
	metricValue: number,
	costRate: number
): number {
	return metricValue * costRate;
}

/**
 * Format current date as YYYY-MM billing period string.
 */
function getBillingPeriod(): string {
	const now = new Date();
	const year = now.getFullYear();
	const month = String(now.getMonth() + 1).padStart(2, "0");
	return `${year}-${month}`;
}

/**
 * Create and persist a usage record via GraphQL mutation.
 * Associates the record with the authenticated Cognito user identity.
 * Retries once on failure and logs errors.
 */
export async function createUsageRecord(
	input: UsageRecordInput
): Promise<UsageRecord> {
	const client = generateClient({ authMode: "userPool" });

	const authSession = await fetchAuthSession();

	let currentUser: { username: string; userId: string | undefined } = {
		username: "",
		userId: undefined,
	};
	try {
		const user = await getCurrentUser();
		currentUser = { username: user.username, userId: user.userId };
	} catch {
		// Fall back to defaults if user unavailable
	}

	const userId =
		(authSession.tokens?.idToken?.payload?.sub as string) ??
		currentUser.userId ??
		authSession.identityId ??
		"";
	const userName = currentUser.username;

	const timestamp = Math.floor(Date.now() / 1000);
	const billingPeriod = getBillingPeriod();
	const estimatedCost = calculateEstimatedCost(
		input.metricValue,
		input.costRateApplied
	);

	const record: UsageRecord = {
		id: uuidv4(),
		userId,
		userName,
		jobId: input.jobId ?? "",
		jobType: input.jobType,
		timestamp,
		metricType: input.metricType,
		metricValue: input.metricValue,
		costRateApplied: input.costRateApplied,
		estimatedCost,
		billingPeriod,
	};

	const variables = {
		input: {
			id: record.id,
			userId: record.userId,
			userName: record.userName,
			jobId: record.jobId,
			jobType: record.jobType,
			timestamp: record.timestamp,
			billingPeriod: record.billingPeriod,
			metricType: record.metricType,
			metricValue: record.metricValue,
			sourceLanguage: input.sourceLanguage,
			targetLanguageCount: input.targetLanguageCount,
			contentType: input.contentType,
			costRateApplied: record.costRateApplied,
			estimatedCost: record.estimatedCost,
		},
	};

	let attempts = 0;
	while (attempts < 2) {
		try {
			await client.graphql({
				query: usageCreateRecord,
				variables,
			});
			return record;
		} catch (error) {
			attempts++;
			if (attempts >= 2) {
				console.error(
					"Failed to persist usage record after retry:",
					error,
					{ jobId: input.jobId, jobType: input.jobType }
				);
			}
		}
	}

	return record;
}
